import React from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useHistory } from "react-router";

import RecoverAppleAccountSheet from "../RecoverAppleAccountSheet";
import {
  setAdoc_t,
  setAppleRecoverPending,
} from "../../redux/reducer/userSlice";
import {
  getUserInfo,
  resetUserInfoThrottle,
} from "../../redux/actions/userActions";
import { fetchAppPermissions } from "../../redux/actions/permissionsActions";
import {
  isOnboardingIncomplete,
  onboardingResumePath,
  hydrateInitialConfigFromProfile,
} from "../../services/onboardingProfile";
import { saveCurrentEnvironment, clearRegistrationTokens } from "../../services/authGuard";
import { syncEntitlementsForRecoveredUser } from "../../services/RevenueCat";

import { removeAuth, writeAuth } from "../../services/authStorage";
/**
 * Global mount for the Apple "Hide My Email" recover interstitial. The sheet
 * can't live on the Login page — RouteAuth immediately redirects an authenticated
 * user off /login, so it would unmount before it could show. Instead it's driven
 * by the `appleRecoverPending` redux flag (set in Login's authCheck after a relay
 * sign-in with incomplete onboarding) and rendered here at the app root, so it
 * overlays whatever route the user landed on (typically onboarding step 1).
 */
const AppleRecoverGate = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();

  const pending = useSelector((s) => s.user?.appleRecoverPending === true);

  const finishRecovery = (profile) => {
    const incomplete = profile ? isOnboardingIncomplete(profile) : true;
    // If the recovered account still needs onboarding, seed the wizard from ITS
    // profile (not the relay account's leftover config), otherwise the goals step
    // submits stale/mismatched data and 422s.
    if (incomplete) hydrateInitialConfigFromProfile(dispatch, profile || {});
    dispatch(setAppleRecoverPending(false));
    history.replace(incomplete ? onboardingResumePath(profile || {}) : "/home");
  };

  return (
    <RecoverAppleAccountSheet
      isOpen={pending}
      onContinueNew={() => {
        // Keep the relay account; the user is already on the onboarding screen
        // beneath this modal, so just dismiss.
        dispatch(setAppleRecoverPending(false));
      }}
      onRecovered={async (token) => {
        // Swap the session to the recovered account.
        dispatch(setAdoc_t(token));
        writeAuth("adoc_t", token);
        saveCurrentEnvironment();
        clearRegistrationTokens();

        // We're switching accounts mid-session: drop the previous (relay)
        // account's cached agreements + profile so getUserInfo re-fetches them
        // for the recovered account. getUserInfo reuses cached `adoc_agreements`
        // when present, so without this a fully-onboarded account reads as
        // "incomplete" (isOnboardingIncomplete's agreements check) and gets
        // wrongly walked through onboarding again.
        removeAuth("adoc_agreements");
        removeAuth("adoc_p");

        try {
          resetUserInfoThrottle();
          const [profileResult] = await Promise.allSettled([
            dispatch(getUserInfo())
              .unwrap()
              .catch((err) => {
                if (err?.name === "ConditionError") return null;
                throw err;
              }),
            dispatch(fetchAppPermissions()).unwrap().catch(() => {}),
          ]);

          // Pull the recovered account's store entitlements so an IAP
          // subscription reappears without a flash of "free" (native / no-op web).
          const recoveredUserId = store.getState()?.user?.user?.id;
          await syncEntitlementsForRecoveredUser(recoveredUserId);

          const profile =
            profileResult.status === "fulfilled" && profileResult.value
              ? profileResult.value
              : store.getState()?.user;

          finishRecovery(profile);
        } catch (e) {
          // Non-fatal: we still have the recovered bearer; drop the gate and
          // route from whatever profile made it into the store.
          finishRecovery(store.getState()?.user);
        }
      }}
    />
  );
};

export default AppleRecoverGate;
